import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Loader2, Lock } from "lucide-react";
import { AUDIT_STEPS, INDUSTRIES, CONNECTORS, MARQUEE, LOGOS } from "@/data";
import { Reveal, SectionHead, CheckItem, Tile, BrandLogo } from "@/components/primitives";
import { requestValidation, DemoLink } from "@/components/Booking";

export const Audit = () => {
  const [email, setEmail] = useState("");
  const [helpdesk, setHelpdesk] = useState("Shared email inbox");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await requestValidation({ email, helpdesk });
      toast.success("Validation request sent", { description: data.confirmation_sent ? "Our team has your request — check your inbox for a confirmation." : "Our team has your request and will reply within one business day." });
      setEmail("");
    } catch (err) {
      toast.error("Couldn't start the validation", { description: err?.response?.status === 429 ? "Too many requests — please try again in a while." : "Please check your email address." });
    } finally {
      setLoading(false);
    }
  };
  return (
    <section id="audit" className="relative py-24 lg:py-32 deep-section text-white overflow-hidden" data-testid="section-audit">
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="absolute -left-32 top-10 w-[480px] h-[480px] rounded-full border border-white/10 orbit-ring" style={{ animationDuration: "60s" }}>
        <span className="absolute top-1/2 -right-2 w-4 h-4 rounded-full bg-violet-300 shadow-[0_0_30px_#c4b5fd]" />
      </div>
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-6">
          <SectionHead dark eyebrow="Free support audit" title={<>Validate your customer support operations through us. <em className="italic font-light text-violet-300">For free.</em></>} desc="Connect your help desk and Quantum Desk replays your last 30 days of conversations through its agents. You see exactly what would have been answered in seconds, what would have been escalated, and what it would have cost you — before you change a thing." />
          <Reveal delay={0.1} className="mt-8">
            <form onSubmit={submit} className="glass-dark rounded-2xl p-4 flex flex-col sm:flex-row gap-3" data-testid="audit-form">
              <select value={helpdesk} onChange={(e) => setHelpdesk(e.target.value)} className="rounded-xl bg-white/10 border border-white/15 px-3 py-3 text-sm text-white focus:outline-none focus:border-white/60 [&>option]:text-black" data-testid="audit-helpdesk">
                {["Shared email inbox", "Gmail / Outlook", "HubSpot", "Shopify Inbox", "Other help desk"].map((h) => <option key={h}>{h}</option>)}
              </select>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="flex-1 rounded-xl bg-white/10 border border-white/15 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-white/60" data-testid="audit-email" />
              <button disabled={loading} className="btn-primary !bg-white !text-[var(--qd-accent)] justify-center" data-testid="audit-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Start the free validation <ArrowRight className="w-4 h-4" />
              </button>
            </form>
            <p className="mt-3 text-xs text-white/50 inline-flex items-center gap-1.5"><Lock className="w-3 h-3" /> Read-only access — nothing is sent to your customers</p>
          </Reveal>
        </div>
        <div className="lg:col-span-6 space-y-4 lg:pt-8">
          {AUDIT_STEPS.map((s, i) => (
            <Reveal key={s.n} delay={0.1 + i * 0.12}>
              <div className="glass-dark card-lift rounded-3xl p-6 flex gap-5" data-testid={`audit-step-${s.n}`}>
                <span className="font-display text-4xl text-violet-300 w-10 shrink-0">{s.n}</span>
                <div>
                  <h3 className="font-display text-xl">{s.title}</h3>
                  <p className="text-sm text-white/65 mt-2 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Industries = () => {
  const [active, setActive] = useState(0);
  const ind = INDUSTRIES[active];
  return (
    <section id="industries" className="relative py-24 lg:py-32" data-testid="section-industries">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHead eyebrow="Industry use cases" title={<>Built for the support desks that <em className="italic font-light text-[var(--qd-accent)]">never close</em></>} desc="The same agents, trained on the questions your industry actually gets. Pick yours." />
        <Reveal delay={0.1} className="mt-10 flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap" role="tablist">
          {INDUSTRIES.map((x, i) => (
            <button key={x.key} role="tab" aria-selected={i === active} onClick={() => setActive(i)} className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold border transition-all ${i === active ? "bg-[var(--qd-ink)] text-white border-[var(--qd-ink)]" : "glass border-transparent hover:border-[var(--qd-accent)]"}`} data-testid={`industry-tab-${x.key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {x.key}
            </button>
          ))}
        </Reveal>
        <AnimatePresence mode="wait">
          <motion.div key={ind.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.45 }} className="mt-8 glass rounded-3xl overflow-hidden grid lg:grid-cols-12" data-testid="industry-panel">
            <div className="lg:col-span-7 p-7 sm:p-10">
              <p className="eyebrow mb-3">{ind.sub}</p>
              <h3 className="font-display text-3xl sm:text-4xl" data-testid="industry-panel-title">{ind.key}</h3>
              <p className="mt-4 text-[var(--qd-muted)] leading-relaxed">{ind.desc}</p>
              <ul className="mt-6 space-y-3">{ind.points.map((p) => <CheckItem key={p}>{p}</CheckItem>)}</ul>
              <DemoLink ctx={{ source: `industry-${ind.key}` }} className="btn-primary mt-8" testId="industry-cta">
                See the {ind.key.toLowerCase()} demo
              </DemoLink>
            </div>
            <div className="lg:col-span-5 relative min-h-[320px] bg-[#f3f1fd]">
              <img src={ind.img} alt={`${ind.key} illustration`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
              <div className="absolute inset-x-6 bottom-6 grid grid-cols-2 gap-3">
                {ind.stats.map((s) => (
                  <div key={s.l} className="rounded-2xl p-4 bg-white/90 border border-[var(--qd-line)]">
                    <p className="font-display text-2xl text-[var(--qd-accent)]">{s.v}</p>
                    <p className="text-[11px] text-[var(--qd-muted)] mt-1 leading-snug">{s.l}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export const Integrations = () => {
  const [sel, setSel] = useState([]);
  const toggle = (n) => setSel((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n]));
  return (
    <section id="integrations" className="relative py-24 lg:py-32 overflow-hidden" data-testid="section-integrations">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <SectionHead eyebrow="Connectors & integrations" title={<>Plugs into the stack you <em className="italic font-light text-[var(--qd-accent)]">already run</em></>} desc="Tap the tools you use. We'll bring that exact stack to your demo." />
          <Reveal delay={0.1} className="shrink-0">
            <span className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 font-mono text-sm" data-testid="integrations-counter">
              <span className="w-2 h-2 rounded-full bg-[var(--qd-accent)]" /> {sel.length} selected
            </span>
          </Reveal>
        </div>
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {CONNECTORS.map((c, i) => (
            <Reveal key={c.name} delay={(i % 6) * 0.05} className="h-full">
              <Tile {...c} selected={sel.includes(c.name)} onClick={() => toggle(c.name)} testId={`connector-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-8">
          <DemoLink ctx={{ source: "integrations", stack: sel }} className="btn-primary" testId="integrations-cta">
            Book a demo with my stack
          </DemoLink>
        </Reveal>
      </div>
      <div className="mt-20 border-y border-[var(--qd-line)] py-5 overflow-hidden" data-testid="marquee">
        <div className="marquee-track gap-14">
          {[...MARQUEE, ...MARQUEE].map((m, i) => (
            <span key={i} className="font-display text-xl text-[var(--qd-ink)]/70 whitespace-nowrap inline-flex items-center gap-14">
              <span className="inline-flex items-center gap-3"><BrandLogo src={LOGOS[m]} name={m} size={26} /> {m}</span>
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--qd-accent)]" />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
